import React, { useReducer, useEffect } from 'react';
import { Container, Box, Typography, TextField, Button, CircularProgress, Alert, Paper, Grid, Avatar, MenuItem } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { getUserProfile, updateUserProfile } from '../services/api';

const initialState = {
  profile: {
    name: '',
    email: '',
    role: '',
    branch: '',
    yearOfStudy: '',
    githubUsername: ''
  },
  loading: true,
  saving: false,
  error: '',
  success: ''
};

const profileReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, profile: action.payload };
    case 'FETCH_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_FIELD':
      return {
        ...state,
        profile: { ...state.profile, [action.field]: action.value }
      };
    case 'SAVE_START':
      return { ...state, saving: true, error: '', success: '' };
    case 'SAVE_SUCCESS':
      return { ...state, saving: false, success: action.payload };
    case 'SAVE_FAILURE':
      return { ...state, saving: false, error: action.payload };
    default:
      return state;
  }
};

const Profile = () => {
  
  const [state, dispatch] = useReducer(profileReducer, initialState);
  const { profile, loading, saving, error, success } = state;

  useEffect(() => {
    const fetchProfile = async () => {
      dispatch({ type: 'FETCH_START' });
      try {
        const response = await getUserProfile();
        
        const userData = response.data.user;
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role || '',
            branch: userData.branch || '',
            yearOfStudy: userData.yearOfStudy || '',
            githubUsername: userData.githubUsername || '',
            profilePic: userData.profilePic
          }
        });
      } catch (err) {
        console.error('Error fetching profile:', err);
        dispatch({ type: 'FETCH_FAILURE', payload: 'Failed to load profile data.' });
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: 'UPDATE_FIELD', field: name, value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch({ type: 'SAVE_START' });

    try {
      await updateUserProfile({
        branch: profile.branch,
        yearOfStudy: profile.yearOfStudy,
        githubUsername: profile.githubUsername
      });

      dispatch({ type: 'SAVE_SUCCESS', payload: 'Profile updated successfully!' });
    } catch (err) {
      console.error('Error updating profile:', err);
      dispatch({ type: 'SAVE_FAILURE', payload: 'Failed to update profile. Please try again.' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar 
              src={profile.profilePic} 
              alt={profile.name}
              sx={{ width: 80, height: 80, mr: 3 }}
            />
            <Box>
              <Typography variant="h4" component="h1">
                My Profile
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Manage your account details
              </Typography>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              {/* Read-only Fields */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={profile.name}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={profile.email}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Role"
                  value={profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>

              
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Branch / Department"
                  name="branch"
                  value={profile.branch}
                  onChange={handleChange}
                  helperText="Select your branch"
                >
                  <MenuItem value="CSE - Computer Science & engineering">CSE - Computer Science & engineering</MenuItem>
                  <MenuItem value="CSE - AIML">CSE - AIML</MenuItem>
                  <MenuItem value="ECE">ECE - Electronics and Communication Engineering</MenuItem>
                  <MenuItem value="EEE">EEE - Electrical and Electronics Engineering</MenuItem>
                  <MenuItem value="IT">IT - Information Technology</MenuItem>
                </TextField>
              </Grid>
              {profile.role === 'student' && <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Year of Study"
                  name="yearOfStudy"
                  type="number"
                  value={profile.yearOfStudy}
                  onChange={handleChange}
                  placeholder="e.g., 3"
                />
              </Grid>}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="GitHub Username"
                  name="githubUsername"
                  value={profile.githubUsername}
                  onChange={handleChange}
                  placeholder="e.g., octocat"
                  helperText="Used to link your projects to your GitHub profile"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Profile;
